'use client';

import { FC, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslations } from 'next-intl';
import { toast } from '@/hotdesk-ui/components/toast-v4';
import { Spinner } from 'react-bootstrap';
import { isValidPhoneNumber } from 'libphonenumber-js';
import IntlTelInput from 'react-intl-tel-input';
import { User } from 'hotdesk_typescript';
import { UserService } from '@/hotdesk-ui/services/user.service';
import { AuthService } from '@/hotdesk-ui/services/auth.service';
import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { getUserLocale } from '@/hotdesk-ui/lib/language.service';
import { setUserData } from '@/hotdesk-ui/store/slices/auth.slice';
import { dispatch } from '@/hotdesk-ui/store/store';
import HotdeskButton from '@/hotdesk-ui/components/shared/HotdeskButton';
import { VerifyPhoneModalV4 } from '@/hotdesk-ui/components/auth-v4';
import Modal from './Modal';
import modalStyles from './Modal.module.scss';
import { useDefaultPhoneCountry } from '@/hotdesk-ui/hooks/useDefaultPhoneCountry';
import styles from './EditPhoneModal.module.scss';
interface EditPhoneModalProps {
  show: boolean;
  onClose: () => void;
  user: User;
  language?: string;
  /** Fired after the new number is verified (OTP accepted). */
  onVerified?: () => void;
}
interface FormSchema {
  phoneNumber: string;
}
const getAuthService = async (languageCode: string): Promise<AuthService> => {
  const apiConfig = await new ApiConfigration().getApiConfig(languageCode);
  return new AuthService(apiConfig);
};
const getBackendErrorMessage = (error: any, t: ReturnType<typeof useTranslations>): string => {
  const body = error?.body || error?.response?.data || error?.data;
  if (body?.detail && typeof body.detail === 'string') {
    return body.detail;
  }
  if (body?.phoneNumber && Array.isArray(body.phoneNumber) && body.phoneNumber[0]) {
    const message = body.phoneNumber[0];
    if (typeof message !== 'string') {
      return t('my-account.edit-phone.error');
    }
    if (message.toLowerCase().includes('unique')) {
      return t('my-account.edit-phone.unique-error');
    }
    if (message === 'phone_not_mobile_error') {
      return t('auth.signUp.invalidPhoneNumber');
    }
    if (message === 'Enter a valid phone number.') {
      return t('auth.signUp.phone-error');
    }
    return message;
  }
  if (body?.nonFieldErrors?.[0]) {
    return body.nonFieldErrors[0];
  }
  return error?.message || t('my-account.edit-phone.error');
};
const EditPhoneModal: FC<EditPhoneModalProps> = ({
  show,
  onClose,
  user,
  language = 'en',
  onVerified
}) => {
  const t = useTranslations();
  const defaultPhoneCountry = useDefaultPhoneCountry().toLowerCase();
  const [isLoading, setIsLoading] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [submittedPhone, setSubmittedPhone] = useState<string | undefined>(undefined);
  const schema = yup.object().shape({
    phoneNumber: yup.string().required(t('auth.signUp.phone') + ' ' + t('auth.signUp.error')).test('valid-phone', t('auth.signUp.phone-error'), value => !!value && isValidPhoneNumber(value))
  });
  const {
    handleSubmit,
    reset,
    trigger,
    formState: {
      errors,
      touchedFields
    },
    control
  } = useForm<FormSchema>({
    defaultValues: {
      phoneNumber: user?.phoneNumber ?? ''
    },
    resolver: yupResolver(schema)
  });
  useEffect(() => {
    if (show) reset({
      phoneNumber: user?.phoneNumber ?? ''
    });
  }, [show, user, reset]);
  const sendVerification = async (phoneNumber: string): Promise<boolean> => {
    try {
      const authService = await getAuthService('en');
      await authService.sendPhoneCode({
        username: user.username,
        phoneNumber: phoneNumber.replace(/\s+/g, '')
      });
      return true;
    } catch (error: any) {
      const errorMessage = getBackendErrorMessage(error, t);
      toast.error(errorMessage, {
        position: 'bottom-center'
      });
      console.error('Phone verification error', error, errorMessage);
      return false;
    }
  };
  const onSubmit = async (data: FormSchema) => {
    setIsLoading(true);
    try {
      const languageCode = await getUserLocale();
      const apiConfig = await new ApiConfigration().getApiConfig(languageCode);
      const userService = new UserService(apiConfig);
      const response = await userService.updateUser(user.id ?? 0, {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        username: user.username,
        phoneNumber: data.phoneNumber
      });
      if (response) {
        dispatch(setUserData(response));
        toast.success(t('my-account.edit-phone.success'), {
          position: 'bottom-center'
        });
        const verificationSent = await sendVerification(data.phoneNumber);
        if (verificationSent) {
          setSubmittedPhone(data.phoneNumber);
          onClose();
          setVerifyOpen(true);
        }
      }
    } catch (error: any) {
      console.error('Error updating phone', error);
      toast.error(getBackendErrorMessage(error, t), {
        position: 'bottom-center'
      });
    } finally {
      setIsLoading(false);
    }
  };
  return <>
            <Modal show={show} onClose={onClose} title={user?.phoneNumber ? t('my-account.edit-phone.title') : t('my-account.edit-phone.add-title')} allowOverflow data-sentry-element="Modal" data-sentry-source-file="EditPhoneModal.tsx">
                <form className={modalStyles.body} onSubmit={handleSubmit(onSubmit)}>
                    <div className={`${modalStyles.field} ${styles.phoneField}`}>
                        <label className={modalStyles.label}>{t('my-account.phone')}</label>
                        <div className={styles.intlWrap}>
                            <Controller name="phoneNumber" control={control} render={({
              field: {
                onChange
              }
            }) => <IntlTelInput
            // Uncontrolled: passing `value` back made every
            // keystroke reformat the field and jump the caret
            // (e.g. after the dial code appears). The modal
            // unmounts on close, so `defaultValue` re-seeds
            // from the user's saved number on each open.
            key={defaultPhoneCountry} fieldName="phoneNumber" defaultCountry={defaultPhoneCountry} defaultValue={user?.phoneNumber?.startsWith('+') ? user.phoneNumber : ''} placeholder={t('auth.signUp.phone')} containerClassName="intl-tel-input" preferredCountries={['ae', 'gb', 'eg']} useMobileFullscreenDropdown={false} onPhoneNumberChange={(_isValid, _number, _country, fullNumber) => {
              onChange(fullNumber);
            }} onPhoneNumberBlur={() => {
              trigger('phoneNumber');
            }} inputClassName={`form-control w-100 ${errors.phoneNumber ? 'invalid' : ''} ${touchedFields.phoneNumber ? 'touched' : ''}`} />} data-sentry-element="Controller" data-sentry-source-file="EditPhoneModal.tsx" />
                        </div>
                        {errors.phoneNumber && <span className={modalStyles.error}>{errors.phoneNumber.message}</span>}
                    </div>

                    <div className={modalStyles.actions}>
                        <HotdeskButton as="button" variant="secondary" size="lg" fullWidth type="button" onClick={onClose} disabled={isLoading} data-sentry-element="HotdeskButton" data-sentry-source-file="EditPhoneModal.tsx">
                            {t('my-account.cancel')}
                        </HotdeskButton>
                        <HotdeskButton as="button" variant="primary" size="lg" fullWidth type="submit" disabled={isLoading} data-sentry-element="HotdeskButton" data-sentry-source-file="EditPhoneModal.tsx">
                            {isLoading ? <Spinner animation="border" size="sm" /> : t('my-account.edit-phone.save')}
                        </HotdeskButton>
                    </div>
                </form>
            </Modal>

            <VerifyPhoneModalV4 show={verifyOpen} handleClose={() => setVerifyOpen(false)} language={language} userId={user?.id} phoneNumber={submittedPhone ?? user?.phoneNumber ?? ''} onVerified={onVerified} data-sentry-element="VerifyPhoneModalV4" data-sentry-source-file="EditPhoneModal.tsx" />
        </>;
};
export default EditPhoneModal;